import { format as dfFormat } from "date-fns";

/**
 * Calendar dates (event start/end, deadlines) are stored as Postgres `date`
 * values — "2026-04-11" means that day, everywhere. Passing them to
 * `new Date()` parses them as UTC midnight, which renders as the previous day
 * for anyone west of UTC. Always route these values through the helpers here.
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Parse a calendar date (or timestamp) into a Date anchored in local time. */
export const parseCalendarDate = (
  value: string | Date | null | undefined
): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  // Date-only, or a timestamp we only care about the calendar day of.
  const m = trimmed.match(DATE_ONLY) ?? trimmed.slice(0, 10).match(DATE_ONLY);
  if (m && (trimmed.length === 10 || /^\d{4}-\d{2}-\d{2}T00:00:00/.test(trimmed))) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }

  const d = new Date(trimmed);
  return isNaN(d.getTime()) ? null : d;
};

/** Format a calendar date without any timezone shifting. */
export const formatCalendarDate = (
  value: string | Date | null | undefined,
  formatStr = "MMM d, yyyy",
  fallback = ""
): string => {
  const d = parseCalendarDate(value);
  return d ? dfFormat(d, formatStr) : fallback;
};

/** "Apr 11 – 12, 2026" style range, collapsing shared month/year. */
export const formatCalendarRange = (
  start: string | Date | null | undefined,
  end: string | Date | null | undefined,
  fallback = "Dates TBD"
): string => {
  const s = parseCalendarDate(start);
  const e = parseCalendarDate(end);
  if (!s && !e) return fallback;
  if (!s) return formatCalendarDate(e, "MMM d, yyyy", fallback);
  if (!e || s.getTime() === e.getTime()) return dfFormat(s, "MMM d, yyyy");
  if (s.getFullYear() === e.getFullYear()) {
    return s.getMonth() === e.getMonth()
      ? `${dfFormat(s, "MMM d")} – ${dfFormat(e, "d, yyyy")}`
      : `${dfFormat(s, "MMM d")} – ${dfFormat(e, "MMM d, yyyy")}`;
  }
  return `${dfFormat(s, "MMM d, yyyy")} – ${dfFormat(e, "MMM d, yyyy")}`;
};

/** YYYY-MM-DD for a Date, using local fields (safe for writing back to `date` columns). */
export const toCalendarDateString = (
  value: string | Date | null | undefined
): string | null => {
  const d = parseCalendarDate(value);
  return d ? dfFormat(d, "yyyy-MM-dd") : null;
};

/**
 * Times are stored as 24-hour `HH:MM` strings (schedule_constraints.parameters,
 * scheduled_games.scheduled_start_time, session windows). Coaches and directors
 * read 12-hour clock, so every render goes through here: "16:57" → "4:57PM",
 * "09:05" → "9:05AM". Storage stays 24-hour — this only formats.
 */
const TIME_ONLY = /^(\d{1,2}):(\d{2})/;

export const formatTime = (
  value: string | Date | null | undefined,
  fallback = ""
): string => {
  if (value === null || value === undefined || value === "") return fallback;

  let hours: number;
  let minutes: number;

  if (value instanceof Date) {
    if (isNaN(value.getTime())) return fallback;
    hours = value.getHours();
    minutes = value.getMinutes();
  } else {
    const raw = String(value).trim();
    // "HH:MM", "HH:MM:SS", or the time half of an ISO timestamp.
    const m = raw.match(TIME_ONLY) ?? raw.match(/T(\d{2}):(\d{2})/);
    if (m) {
      hours = Number(m[1]);
      minutes = Number(m[2]);
    } else {
      const d = new Date(raw);
      if (isNaN(d.getTime())) return fallback || raw;
      hours = d.getHours();
      minutes = d.getMinutes();
    }
  }

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return fallback;
  const suffix = hours >= 12 ? "PM" : "AM";
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${h12}:${String(minutes).padStart(2, "0")}${suffix}`;
};

/** "4:20PM – 7:20PM" for a stored start/end window. */
export const formatTimeRange = (
  start: string | Date | null | undefined,
  end: string | Date | null | undefined,
  fallback = ""
): string => {
  const s = formatTime(start);
  const e = formatTime(end);
  if (!s && !e) return fallback;
  if (!e) return s;
  if (!s) return e;
  return `${s} – ${e}`;
};
